import React, { useState } from 'react';
import { Map, Sparkles, Hexagon, ChevronDown, LogOut, User } from 'lucide-react';
import logoSmall from '../NorthAI_Logo_Small_Zoomed.png';

/**
 * PlatformHome — Product launcher for NorthAI suite users.
 *
 * Full-screen dark page shown before the user enters a specific product.
 * No sidebar — replaces the entire app shell.
 *
 * Props:
 *   currentUser       — user object from /api/users/me
 *   onEnterGeoAgent   — callback to enter GeoAgent map view
 *   onEnterConcierge  — callback to navigate to Lead Concierge (with JWT handoff)
 *   onLogout          — callback to log out
 */
const PlatformHome = ({ currentUser, onEnterGeoAgent, onEnterConcierge, onLogout }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // ── Derived display values ──────────────────────────────────────────────────
  const firstName =
    currentUser?.first_name || currentUser?.email?.split('@')[0] || 'there';

  const fullName =
    currentUser?.first_name && currentUser?.last_name
      ? `${currentUser.first_name} ${currentUser.last_name}`
      : currentUser?.first_name || currentUser?.email || 'User';

  const avatarInitial = (currentUser?.first_name || currentUser?.email || 'U')
    .charAt(0)
    .toUpperCase();

  // Normalize role for display
  const roleDisplay = {
    admin: 'Admin',
    salesperson: 'Salesperson',
    area_manager: 'Area Manager',
    hq_manager: 'HQ Manager',
  }[currentUser?.role] || currentUser?.role || 'User';

  // ── Time-of-day greeting ────────────────────────────────────────────────────
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // ── Date line ───────────────────────────────────────────────────────────────
  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  // ── Role-based access ───────────────────────────────────────────────────────
  const role = currentUser?.role || '';
  const hasGeoAgent = true; // all authenticated users can use GeoAgent
  const hasConcierge = role === 'admin'; // Concierge is admin-only

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleEnterGeoAgent = () => {
    if (hasGeoAgent) onEnterGeoAgent?.();
  };

  const handleEnterConcierge = () => {
    if (hasConcierge) onEnterConcierge?.();
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    onLogout?.();
  };

  // ── Card definitions ────────────────────────────────────────────────────────
  const allCards = [
    {
      key: 'geoagent',
      title: 'GeoAgent',
      subtitle: 'Field sales intelligence',
      icon: Map,
      iconGradient: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
      btnGradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      btnHover: '#1d4ed8',
      active: true,
      onEnter: handleEnterGeoAgent,
      btnLabel: 'Open GeoAgent',
    },
    // Concierge card only visible to admins
    ...(hasConcierge ? [{
      key: 'concierge',
      title: 'Lead Concierge',
      subtitle: 'AI-powered lead matching',
      icon: Sparkles,
      iconGradient: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
      btnGradient: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
      btnHover: '#6d28d9',
      active: true,
      onEnter: handleEnterConcierge,
      btnLabel: 'Open Lead Concierge',
    }] : []),
    {
      key: 'territory',
      title: 'Territory Service',
      subtitle: 'Coming soon',
      icon: Hexagon,
      iconGradient: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
      btnGradient: null,
      btnHover: null,
      active: false,
      comingSoon: true,
      onEnter: null,
      btnLabel: 'Not Yet Available',
    },
  ];
  const cards = allCards;

  return (
    /* Full-screen dark background */
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#030712', // gray-950
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Subtle background glow blobs (purely decorative) ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '-20%',
          left: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          bottom: '-20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* ── Top Bar ── */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: '56px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* Logo + wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src={logoSmall}
            alt="NorthAI"
            style={{ height: '28px', width: '28px', objectFit: 'contain' }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: '16px',
              background: 'linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.3px',
            }}
          >
            NorthAI
          </span>
        </div>

        {/* User pill + dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px 6px 6px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)',
              cursor: 'pointer',
              color: '#e5e7eb',
              fontSize: '13px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'rgba(255,255,255,0.09)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')
            }
            aria-label="User menu"
            aria-expanded={userMenuOpen}
          >
            {/* Avatar initial */}
            <span
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {avatarInitial}
            </span>
            <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fullName}
            </span>
            {/* Role badge */}
            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#94a3b8',
                background: 'rgba(148,163,184,0.12)',
                borderRadius: '999px',
                padding: '1px 7px',
                flexShrink: 0,
              }}
            >
              {roleDisplay}
            </span>
            <ChevronDown
              size={14}
              style={{
                color: '#6b7280',
                transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
                flexShrink: 0,
              }}
            />
          </button>

          {/* Dropdown */}
          {userMenuOpen && (
            <>
              {/* Backdrop to close on outside click */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 19 }}
                onClick={() => setUserMenuOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  minWidth: '200px',
                  background: '#111827',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  zIndex: 20,
                  overflow: 'hidden',
                  padding: '6px',
                }}
              >
                {/* User info row */}
                <div
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    marginBottom: '4px',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#f9fafb' }}>
                    {fullName}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                    {currentUser?.email}
                  </div>
                </div>

                {/* Profile item (visual only for now) */}
                <DropdownItem icon={User} label="Profile" onClick={() => setUserMenuOpen(false)} />

                {/* Logout */}
                <DropdownItem
                  icon={LogOut}
                  label="Sign out"
                  onClick={handleLogout}
                  danger
                />
              </div>
            </>
          )}
        </div>
      </header>

      {/* ── Hero Greeting ── */}
      <div
        style={{
          textAlign: 'center',
          padding: '56px 24px 48px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(1.75rem, 5vw, 2rem)',
            fontWeight: 700,
            color: '#f9fafb',
            margin: 0,
            letterSpacing: '-0.5px',
          }}
        >
          {greeting}, {firstName} 👋
        </h1>
        <p
          style={{
            marginTop: '8px',
            fontSize: '14px',
            color: '#6b7280',
          }}
        >
          {todayLabel}
        </p>
      </div>

      {/* ── Product Cards ── */}
      <main
        style={{
          flex: 1,
          padding: '0 16px 48px',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/*
          Responsive grid:
          - Mobile (default): single column, cards stacked
          - md+: 3 columns side-by-side
          Using a CSS custom-property trick with inline style for responsive behavior.
          We'll use a flex-wrap approach so it works without media query JS.
        */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            width: '100%',
            maxWidth: '900px',
          }}
        >
          {cards.map((card) => (
            <ProductCard
              key={card.key}
              card={card}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

// ── ProductCard ─────────────────────────────────────────────────────────────

/**
 * ProductCard — dark card with gradient icon bar, title, subtitle, and CTA.
 *
 * Props:
 *   card — card definition object (see cards array above)
 */
const ProductCard = ({ card }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = card.icon;

  const isClickable = card.active && card.onEnter;

  const handleClick = () => {
    if (isClickable) card.onEnter();
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#111827', // gray-900
        border: `1px solid ${hovered && isClickable ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: isClickable ? 'pointer' : 'default',
        transform: hovered && isClickable ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered && isClickable
          ? '0 20px 40px rgba(0,0,0,0.4)'
          : '0 4px 12px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={isClickable ? `Open ${card.title}` : undefined}
    >
      {/* Gradient icon area */}
      <div
        style={{
          height: '80px',
          background: card.iconGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: card.active ? 1 : 0.5,
          flexShrink: 0,
        }}
      >
        <Icon size={32} color="#ffffff" strokeWidth={1.75} />
      </div>

      {/* Card body */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: card.active ? '#f9fafb' : '#6b7280',
              letterSpacing: '-0.3px',
            }}
          >
            {card.title}
          </span>

          {/* Coming soon badge */}
          {card.comingSoon && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6b7280',
                background: 'rgba(107,114,128,0.15)',
                border: '1px solid rgba(107,114,128,0.2)',
                borderRadius: '999px',
                padding: '1px 8px',
              }}
            >
              Coming soon
            </span>
          )}

          {/* Upgrade badge for inaccessible active products */}
          {!card.active && !card.comingSoon && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#f59e0b',
                background: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: '999px',
                padding: '1px 8px',
              }}
            >
              Upgrade
            </span>
          )}
        </div>

        {/* Subtitle */}
        <p
          style={{
            fontSize: '13px',
            color: '#6b7280',
            margin: 0,
            lineHeight: 1.5,
            flex: 1,
          }}
        >
          {card.subtitle}
        </p>

        {/* CTA button */}
        <button
          onClick={(e) => {
            // Prevent double-firing when card itself is clicked
            e.stopPropagation();
            if (isClickable) handleClick();
          }}
          disabled={!isClickable}
          style={{
            marginTop: '12px',
            width: '100%',
            padding: '11px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: isClickable ? 'pointer' : 'not-allowed',
            background: isClickable
              ? card.btnGradient
              : 'rgba(31,41,55,0.8)', // gray-800 equivalent
            color: isClickable ? '#ffffff' : '#4b5563', // gray-600
            transition: 'opacity 0.15s',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            minHeight: '44px', // minimum touch target
          }}
          onMouseEnter={(e) => {
            if (isClickable) e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          {card.btnLabel}
          {isClickable && (
            <span style={{ fontSize: '14px', lineHeight: 1 }}>→</span>
          )}
        </button>
      </div>
    </div>
  );
};

// ── DropdownItem ─────────────────────────────────────────────────────────────

const DropdownItem = ({ icon: Icon, label, onClick, danger = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '8px 12px',
        borderRadius: '8px',
        border: 'none',
        background: hovered ? (danger ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.05)') : 'transparent',
        color: danger ? (hovered ? '#f87171' : '#9ca3af') : '#d1d5db',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.12s',
        minHeight: '36px',
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
};

export default PlatformHome;
